/**
 * OperationalBundleFailureState
 *
 * Display verification failures and errors.
 * Fail closed — no replay workspace rendered when invalid.
 */

import React from 'react';
import type { BundleVerificationResult } from '../server/export/operationalBundleTypes.js';
import { NEUTRAL, TYPOGRAPHY } from '../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface OperationalBundleFailureStateProps {
  reason: 'malformed' | 'tampered' | 'unsupported' | 'parse-error';
  errorMessage?: string | null;
  verification?: BundleVerificationResult | null;
  onReset: () => void;
}

export const OperationalBundleFailureState: React.FC<OperationalBundleFailureStateProps> = ({
  reason,
  errorMessage,
  verification,
  onReset,
}) => {
  const titleMap: Record<string, string> = {
    malformed: 'INVALID OPERATIONAL BUNDLE',
    tampered: 'BUNDLE INTEGRITY MISMATCH',
    unsupported: 'UNSUPPORTED BUNDLE VERSION',
    'parse-error': 'FAILED TO LOAD BUNDLE',
  };

  const colorMap: Record<string, { border: string; text: string; bg: string }> = {
    malformed: { border: '#ef4444', text: '#ef4444', bg: '#ef444410' },
    tampered: { border: '#ef4444', text: '#ef4444', bg: '#ef444410' },
    unsupported: { border: '#f59e0b', text: '#f59e0b', bg: '#f59e0b10' },
    'parse-error': { border: '#ef4444', text: '#ef4444', bg: '#ef444410' },
  };

  const colors = colorMap[reason];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 20,
      padding: 48,
    }}>
      <div style={{
        maxWidth: 560,
        width: '100%',
        border: `1px solid ${colors.border}50`,
        borderRadius: 6,
        padding: 32,
        backgroundColor: colors.bg,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: colors.text,
          marginBottom: 12,
          textAlign: 'center',
        }}>
          {titleMap[reason]}
        </div>

        {errorMessage && (
          <div style={{
            fontSize: TYPOGRAPHY.body.size,
            color: NEUTRAL.text400,
            marginBottom: 16,
            textAlign: 'center',
            lineHeight: 1.5,
          }}>
            {errorMessage}
          </div>
        )}

        {verification && verification.errors.length > 0 && (
          <div style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: '#ef444410',
            border: '1px solid #ef444430',
            borderRadius: 4,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: '#ef4444',
              marginBottom: 8,
            }}>
              Verification Failures
            </div>
            {verification.errors.map((err, i) => (
              <div key={i} style={{
                fontSize: TYPOGRAPHY.label.size,
                color: '#ef4444',
                marginBottom: 4,
                paddingLeft: 12,
                position: 'relative',
              }}>
                <span style={{ position: 'absolute', left: 0 }}>·</span>
                {err}
              </div>
            ))}
          </div>
        )}

        {verification && verification.warnings.length > 0 && (
          <div style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: '#f59e0b10',
            border: '1px solid #f59e0b30',
            borderRadius: 4,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: '#f59e0b',
              marginBottom: 8,
            }}>
              Warnings
            </div>
            {verification.warnings.map((w, i) => (
              <div key={i} style={{
                fontSize: TYPOGRAPHY.label.size,
                color: '#f59e0b',
                marginBottom: 4,
                paddingLeft: 12,
                position: 'relative',
              }}>
                <span style={{ position: 'absolute', left: 0 }}>·</span>
                {w}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <button
            onClick={onReset}
            style={{
              padding: '8px 20px',
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: TYPOGRAPHY.label.weight,
              color: NEUTRAL.text500,
              backgroundColor: NEUTRAL.bg300,
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: 4,
              cursor: 'pointer',
            }}
            type="button"
          >
            Load Another Bundle
          </button>
        </div>
      </div>
    </div>
  );
};
